import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { transportApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Confirm from '@/components/common/Confirm'
import Icon from '@/components/common/Icons'
import { Alert, Empty, ErrorNote, Loading, PageHeader, Toolbar } from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'

/** Every number a service has, main one first. */
const contactNumbers = (service) =>
  [service?.contact_number, service?.contact_number_2, service?.contact_number_3].filter(Boolean)

/**
 * The transporters an order can go out with, kept per city.
 *
 * Customers choose from this list on their order page, and add to it when
 * theirs is missing — those rows carry a 'Customer' tag and the name of who
 * added them, so the shop can check what came in from outside. A service
 * any order names cannot be deleted, only switched off: the order should
 * still say how it travelled.
 */
export default function TransportServices() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const editable = can(user, PERMS.deliveriesWrite)

  const [confirming, setConfirming] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [filters, setFilters] = useState({ search: '', status: '', added_by: '', approval: '' })

  const params = {
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.status ? { is_active: filters.status === 'active' } : {}),
    ...(filters.added_by ? { added_by: filters.added_by } : {}),
    ...(filters.approval ? { is_approved: filters.approval === 'approved' } : {}),
  }
  const narrowed = Object.keys(params).length > 0

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transport-services', params],
    queryFn: () => transportApi.list(params),
  })

  const done = (message) => {
    queryClient.invalidateQueries({ queryKey: ['transport-services'] })
    setError('')
    setNotice(message)
  }

  // `message` rides along with the variables for the success notice and is
  // not sent to the API.
  const save = useMutation({
    mutationFn: ({ id, message: _message, ...payload }) =>
      id ? transportApi.update(id, payload) : transportApi.create(payload),
    onSuccess: (service, variables) => {
      const only = Object.keys(variables).length === 2
      done(
        variables.message ??
          (only && 'is_active' in variables
            ? `${service.name} switched ${service.is_active ? 'on' : 'off'}.`
            : `Saved ${service.name}.`),
      )
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: transportApi.remove,
    onSuccess: () => done('Transport service deleted.'),
    onError: (e) => setError(errorMessage(e)),
  })

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }))

  return (
    <div className="transport-page">
      <PageHeader
        title="Transport services"
        subtitle={
          data
            ? `${data.length} service${data.length === 1 ? '' : 's'}` +
              (narrowed ? ' match these filters' : '')
            : 'The transporters customers can choose for their orders'
        }
        actions={
          editable && (
            <Link to="/transport-services/new" className="btn btn-primary btn-sm">
              Add transport service
            </Link>
          )
        }
      />

      <Toolbar>
        <input
          type="search"
          placeholder="Name, number or city…"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">Active and inactive</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={filters.added_by}
          onChange={(e) => setFilter('added_by', e.target.value)}
        >
          <option value="">Added by anyone</option>
          <option value="shop">Added by the shop</option>
          <option value="customer">Added by customers</option>
        </select>
        <select
          value={filters.approval}
          onChange={(e) => setFilter('approval', e.target.value)}
        >
          <option value="">Approved and pending</option>
          <option value="pending">Awaiting approval</option>
          <option value="approved">Approved</option>
        </select>
        {narrowed && (
          <button
            type="button"
            className="btn btn-sm btn-icon"
            title="Clear all filters"
            aria-label="Clear all filters"
            onClick={() => setFilters({ search: '', status: '', added_by: '', approval: '' })}
          >
            <Icon name="clearFilter" />
          </button>
        )}
      </Toolbar>

      {error ? (
        <Alert tone="danger" message={error} onClose={() => setError('')} />
      ) : (
        <Alert tone="success" message={notice} onClose={() => setNotice('')} reserve />
      )}


      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load transport services" onRetry={refetch} />}

      {data && !data.length && (
        <Empty
          title={narrowed ? 'No transport services match these filters' : 'No transport services yet'}
          hint={
            narrowed
              ? undefined
              : 'Add the transporters you send orders with, so customers can choose one.'
          }
        />
      )}

      {data && data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Address</th>
              <th>Routes</th>
              <th>Status</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {data.map((service) => (
              <tr key={service.id} className={service.is_active ? '' : 'inactive'}>
                <td>
                  <Link to={`/transport-services/${service.id}`} className="strong">
                    {service.name}
                  </Link>
                  {/* Who added it, when a customer did: an icon rather than the
                      word 'Customer' on every such row. */}
                  {service.added_by_customer_id && (
                    <div
                      className="cell-sub added-by"
                      title={`Added by customer ${service.added_by_customer_name ?? '(removed)'}`}
                    >
                      <PersonIcon />
                      {service.added_by_customer_name ?? 'a removed customer'}
                    </div>
                  )}
                </td>
                <td className="mono nowrap">
                  {contactNumbers(service).map((number) => (
                    <div key={number}>
                      <a href={`tel:${number}`}>{number}</a>
                    </div>
                  ))}
                </td>
                {/* One address: the street part, then city and state. */}
                <td className="pre-line">
                  {[service.address, service.city, service.state].filter(Boolean).join(', ')}
                </td>
                <td>
                  {service.routes?.length ? (
                    service.routes.map((route) => (
                      <div key={route.id} className="cell-sub route-summary">
                        {route.summary}
                      </div>
                    ))
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {service.is_active ? 'Active' : 'Inactive'}
                  {/* Pending, or — switched off without ever being approved —
                      rejected. */}
                  {!service.is_approved && (
                    <div className="cell-sub">
                      {service.is_active ? (
                        <span className="tag-pending">Awaiting approval</span>
                      ) : (
                        <span className="tag-rejected">Not approved</span>
                      )}
                    </div>
                  )}
                </td>
                {editable && (
                  <td className="right nowrap">
                    <div className="row-actions">
                      {/* Approving happens on the service's page: it shows what
                          the customer typed, and offers Reject / Save & approve
                          while the service is awaiting approval. */}
                      <Link to={`/transport-services/${service.id}`} className="btn btn-sm">
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={save.isPending && save.variables?.id === service.id}
                        onClick={() => {
                          setNotice('')
                          save.mutate({ id: service.id, is_active: !service.is_active })
                        }}
                      >
                        {service.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={service.order_count > 0 || remove.variables === service.id}
                        title={
                          service.order_count > 0
                            ? `On ${service.order_count} order(s) — deactivate it instead`
                            : undefined
                        }
                        onClick={() => {
                          setNotice('')
                          setConfirming(service)
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Confirm
        open={Boolean(confirming)}
        title={`Delete ${confirming?.name ?? ''}?`}
        message="It will no longer be offered to customers. This cannot be undone."
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </div>
  )
}

function PersonIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}
